import PropTypes from 'prop-types';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemText from '@material-ui/core/ListItemText';
import OverflowTooltipContent from '@/component/common/OverflowTooltipContent';
import Utils from '@/common/utils/utils';
import Constant from '@/common/constant/Constant';
import { LOCAL_MCP_PROJECT_PREFIX } from '@/component/mcpCatalog/McpCatalogConstant';
import '../../MultipleSelectCheckmarks.css';
import trI18n from '@/common/utils/trI18n';
import tr from '@/common/utils/tr';
import { useTranslationSubscription } from '@/hooks/useTranslation';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

function MCPServerMenu({
    anchorEl,
    open,
    onClose,
    serverList = [],
    selectedServers = [],
    onToggleServer = () => {},
    anchorOrigin = {
        vertical: 'bottom',
        horizontal: 'left',
    },
    transformOrigin = {
        vertical: 'top',
        horizontal: 'left',
    },
    menuStyle = {},
    disablePortal = false,
}) {
    // Re-render when the translation dictionary loads/extends so server names update.
    useTranslationSubscription();

    const projectServers = serverList.filter(
        server => server.type !== Constant.MCP_SERVER_TYPE.MCP_CATALOG || server.scope === LOCAL_MCP_PROJECT_PREFIX
    );
    const personalServers = serverList.filter(
        server => server.type === Constant.MCP_SERVER_TYPE.MCP_CATALOG && server.scope !== LOCAL_MCP_PROJECT_PREFIX
    );

    const renderServerSection = (servers, title) => {
        if (servers.length === 0) return null;

        return [
            <div key={`${title}-header`} className="list-mcp-subheader">
                {title}
            </div>,
            ...servers.map(server => {
                const isConnected = !!server?.isConnected;
                const isSelected = selectedServers.includes(server.id);
                const serverName =
                    server.displayName || Utils.serverNameWithPrefix(server.name, server.type, server.scope);

                return (
                    <MenuItem
                        key={server.id}
                        selected={isSelected}
                        disabled={!isConnected}
                        onClick={() => isConnected && onToggleServer(server.id)}
                        className="multiple-select-menu-item"
                        role="menuitemcheckbox"
                        aria-checked={isSelected}
                    >
                        <span
                            aria-hidden="true"
                            className={`mcp-server-status-dot ${isConnected ? 'is-connected' : 'is-disconnected'}`}
                        />
                        <ListItemText
                            primary={(
                                <OverflowTooltipContent
                                    text={tr(serverName)}
                                    placement="right"
                                />
                            )}
                        />
                    </MenuItem>
                );
            })
        ];
    };

    return (
        <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={onClose}
            disablePortal={disablePortal}
            PaperProps={{
                className: 'multiple-select-menu-paper',
                'data-component': 'multiple-select-checkmarks',
                style: {
                    maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                    width: 200,
                    ...menuStyle,
                },
            }}
            anchorOrigin={anchorOrigin}
            transformOrigin={transformOrigin}
            getContentAnchorEl={null}
        >
            {renderServerSection(projectServers, trI18n('MCP_SERVER_SETTING_FOR_PROJECT'))}
            {renderServerSection(personalServers, trI18n('MCP_SERVER_SETTING_FOR_PERSONAL'))}
        </Menu>
    );
}

MCPServerMenu.propTypes = {
    anchorEl: PropTypes.any,
    open: PropTypes.bool,
    onClose: PropTypes.func,
    serverList: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            name: PropTypes.string.isRequired,
            type: PropTypes.string.isRequired,
            isConnected: PropTypes.bool,
        })
    ),
    selectedServers: PropTypes.array,
    onToggleServer: PropTypes.func,
    anchorOrigin: PropTypes.object,
    transformOrigin: PropTypes.object,
    menuStyle: PropTypes.object,
    disablePortal: PropTypes.bool,
};

export default MCPServerMenu;
